/**
 * MonitoringCharts — live care monitoring for service users
 *
 * Legal basis:
 *   - CQC Regulation 14 — Meeting nutritional and hydration needs
 *   - NICE CG32 — Nutrition support for adults
 *   - NICE NG45 — Older people in care homes
 *   - EPUAP — Pressure ulcer prevention (repositioning)
 *
 * Chart tabs: Fluid | Food | Repositioning | Weight | Vitals | Bowel | Skin
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Droplets, Utensils, RefreshCw, Plus, Trash2, Clock,
  TrendingUp, TrendingDown, Activity, Thermometer, Heart,
  AlertTriangle, CheckCircle2, Scale, Minus, Info, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'fluid',         label: 'Fluid',         icon: Droplets     },
  { id: 'food',          label: 'Food',           icon: Utensils     },
  { id: 'repositioning', label: 'Reposition',     icon: RefreshCw    },
  { id: 'weight',        label: 'Weight',         icon: Scale        },
  { id: 'vitals',        label: 'Vitals',         icon: Activity     },
  { id: 'bowel',         label: 'Bowel',          icon: Clock        },
];

const POSITIONS = [
  { value: 'left_side',      label: 'Left side'           },
  { value: 'right_side',     label: 'Right side'          },
  { value: 'back',           label: 'Back'                },
  { value: 'semi_recumbent', label: 'Semi-recumbent (30°)'},
  { value: 'sitting',        label: 'Sitting up'          },
  { value: 'standing',       label: 'Standing'            },
  { value: 'upright_chair',  label: 'Upright in chair'    },
];

const MEAL_TYPES = [
  { value: 'breakfast',        label: 'Breakfast'      },
  { value: 'morning_snack',    label: 'Morning snack'  },
  { value: 'lunch',            label: 'Lunch'          },
  { value: 'afternoon_snack',  label: 'Afternoon snack'},
  { value: 'dinner',           label: 'Dinner'         },
  { value: 'evening_snack',    label: 'Evening snack'  },
];

const BRISTOL_TYPES = [
  { value: 1, label: 'Type 1', desc: 'Separate hard lumps (constipation)' },
  { value: 2, label: 'Type 2', desc: 'Lumpy sausage (constipation)' },
  { value: 3, label: 'Type 3', desc: 'Sausage with cracks (normal)' },
  { value: 4, label: 'Type 4', desc: 'Smooth soft sausage (normal)' },
  { value: 5, label: 'Type 5', desc: 'Soft blobs (lacking fibre)' },
  { value: 6, label: 'Type 6', desc: 'Fluffy, mushy pieces (diarrhoea)' },
  { value: 7, label: 'Type 7', desc: 'Watery, no solids (diarrhoea)' },
];

const FLUID_TARGET_ML = 1500;

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const fmt = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' }) : '';
const fmtTime = (t) => { if (!t) return ''; const [h,m] = t.split(':'); const hr = parseInt(h); return `${hr===0?12:hr>12?hr-12:hr}:${m} ${hr<12?'AM':'PM'}`; };
const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => { const dt = new Date(d + 'T12:00:00'); dt.setDate(dt.getDate() + n); return dt.toISOString().slice(0, 10); };

function ProgressBar({ value, max, color = '#0F5C5E', height = 8 }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  const barColor = pct >= 80 ? '#1F8F5F' : pct >= 50 ? '#C78A12' : '#C54B4B';
  return (
    <div className="w-full bg-slate-100 rounded-full overflow-hidden" style={{ height }}>
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: barColor }} />
    </div>
  );
}

// ─── ENTRY ROW ────────────────────────────────────────────────────────────────

function EntryRow({ entry, onDelete, isAdmin }) {
  const chartType = entry.chart_type;
  let summary = '';
  let badge = null;

  if (chartType === 'fluid') {
    const parts = [];
    if (entry.intake_ml) parts.push(`In: ${entry.intake_ml}ml`);
    if (entry.output_ml) parts.push(`Out: ${entry.output_ml}ml`);
    if (entry.drink_type) parts.push(entry.drink_type);
    summary = parts.join(' · ');
    badge = entry.intake_ml ? `+${entry.intake_ml}ml` : null;
  } else if (chartType === 'food') {
    const pct = entry.percentage_eaten;
    summary = `${entry.meal_type?.replace(/_/g, ' ')} — ${pct}% eaten`;
    if (entry.food_description) summary += ` (${entry.food_description})`;
    badge = `${pct}%`;
  } else if (chartType === 'repositioning') {
    summary = `${entry.position_label || entry.position} · Skin: ${entry.skin_check?.replace(/_/g, ' ')}`;
    if (entry.skin_check_area) summary += ` (${entry.skin_check_area})`;
  } else if (chartType === 'weight') {
    summary = `${entry.weight_kg} kg`;
  } else if (chartType === 'vitals') {
    const parts = [];
    if (entry.systolic && entry.diastolic) parts.push(`BP ${entry.systolic}/${entry.diastolic}`);
    if (entry.pulse) parts.push(`Pulse ${entry.pulse}`);
    if (entry.temperature_celsius) parts.push(`Temp ${entry.temperature_celsius}°C`);
    if (entry.blood_glucose_mmol) parts.push(`BGL ${entry.blood_glucose_mmol} mmol`);
    if (entry.oxygen_saturation) parts.push(`SpO₂ ${entry.oxygen_saturation}%`);
    summary = parts.join(' · ');
  } else if (chartType === 'bowel') {
    summary = `Type ${entry.bristol_type} — ${entry.bristol_label || ''}`;
    if (entry.incontinent) summary += ' · Incontinent';
    if (entry.amount) summary += ` · ${entry.amount}`;
  } else if (chartType === 'skin') {
    summary = `${entry.body_area}: ${entry.description}`;
    if (entry.wound_type) summary += ` (${entry.wound_type})`;
  }

  const skinAlert = chartType === 'repositioning' && entry.skin_check && entry.skin_check !== 'intact';

  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border transition-all
      ${skinAlert ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200 hover:border-slate-300'}
    `}>
      <span className="text-xs font-mono text-slate-400 flex-shrink-0">{fmtTime(entry.entry_time)}</span>
      <span className="text-sm text-slate-800 flex-1 min-w-0 truncate">{summary}</span>
      {skinAlert && <Badge className="bg-red-100 text-red-700 text-xs flex-shrink-0">Skin concern</Badge>}
      {badge && !skinAlert && (
        <span className="text-xs font-semibold text-[#0F5C5E] bg-[#D9F1F4]/50 px-2 py-0.5 rounded-lg flex-shrink-0">{badge}</span>
      )}
      {entry.notes && <span className="text-xs text-slate-400 flex-shrink-0 max-w-[120px] truncate">{entry.notes}</span>}
      <span className="text-xs text-slate-400 flex-shrink-0">{entry.recorded_by_name}</span>
      {isAdmin && (
        <button onClick={() => onDelete(entry.id)} className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-400 transition-colors flex-shrink-0">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ─── FLUID CHART ─────────────────────────────────────────────────────────────

function FluidTab({ entries, onAdd, date, isAdmin, onDelete }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ intake_ml: '', output_ml: '', drink_type: '', entry_time: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const total_in  = entries.filter(e => e.intake_ml).reduce((s, e) => s + (e.intake_ml || 0), 0);
  const total_out = entries.filter(e => e.output_ml).reduce((s, e) => s + (e.output_ml || 0), 0);
  const pct = Math.min(100, Math.round((total_in / FLUID_TARGET_ML) * 100));
  const barColor = pct >= 80 ? 'text-[#1F8F5F]' : pct >= 50 ? 'text-amber-600' : 'text-red-600';

  const handleAdd = async () => {
    if (!form.intake_ml && !form.output_ml) { toast.error('Enter intake or output'); return; }
    setSaving(true);
    try {
      await onAdd({ chart_type: 'fluid', entry_date: date, ...form, intake_ml: form.intake_ml ? parseInt(form.intake_ml) : undefined, output_ml: form.output_ml ? parseInt(form.output_ml) : undefined });
      setOpen(false);
      setForm({ intake_ml: '', output_ml: '', drink_type: '', entry_time: '', notes: '' });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* Daily summary */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-slate-500">Fluid intake today</p>
            <p className={`text-2xl font-bold ${barColor}`}>{total_in} <span className="text-sm font-normal text-slate-500">ml of {FLUID_TARGET_ML}ml target</span></p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Output</p>
            <p className="text-lg font-semibold text-slate-700">{total_out} ml</p>
          </div>
        </div>
        <ProgressBar value={total_in} max={FLUID_TARGET_ML} />
        <div className="flex justify-between text-xs text-slate-400 mt-1.5">
          <span>0ml</span>
          <span className={`font-semibold ${barColor}`}>{pct}%</span>
          <span>{FLUID_TARGET_ML}ml (NICE target)</span>
        </div>
        {total_in < FLUID_TARGET_ML * 0.5 && entries.length > 0 && (
          <div className="mt-3 flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-800">Below 50% of daily fluid target — consider prompting fluids</p>
          </div>
        )}
      </div>

      {/* Add button */}
      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-500">{entries.length} record{entries.length !== 1 ? 's' : ''} today</p>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
          <Plus className="h-3.5 w-3.5 mr-1" />Add entry
        </Button>
      </div>

      {/* Entry form */}
      {open && (
        <div className="bg-[#D9F1F4]/20 rounded-2xl border border-[#0F5C5E]/20 p-4 space-y-3">
          <p className="text-xs font-semibold text-[#0F5C5E]">New fluid entry</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Intake (ml)</label>
              <input type="number" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" placeholder="e.g. 200" value={form.intake_ml} onChange={e => setForm(f => ({ ...f, intake_ml: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Output (ml)</label>
              <input type="number" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" placeholder="e.g. 150" value={form.output_ml} onChange={e => setForm(f => ({ ...f, output_ml: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Drink type</label>
              <select className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" value={form.drink_type} onChange={e => setForm(f => ({ ...f, drink_type: e.target.value }))}>
                <option value="">— select —</option>
                {['Water','Tea','Coffee','Juice','Squash','Milk','Soup','Supplement drink'].map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Time</label>
              <input type="time" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" value={form.entry_time} onChange={e => setForm(f => ({ ...f, entry_time: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
              <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" placeholder="Any notes..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={saving} onClick={handleAdd} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
              {saving ? 'Saving…' : 'Add entry'}
            </Button>
          </div>
        </div>
      )}

      {/* Entries */}
      {entries.length === 0 ? (
        <p className="text-center text-xs text-slate-400 py-6">No fluid entries recorded for this date</p>
      ) : (
        <div className="space-y-2">
          {entries.map(e => <EntryRow key={e.id} entry={e} onDelete={id => onDelete(id)} isAdmin={isAdmin} />)}
        </div>
      )}
    </div>
  );
}

// ─── FOOD CHART ───────────────────────────────────────────────────────────────

function FoodTab({ entries, onAdd, date, isAdmin, onDelete }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ meal_type: 'breakfast', percentage_eaten: 75, food_description: '', appetite: '', entry_time: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    setSaving(true);
    try {
      await onAdd({ chart_type: 'food', entry_date: date, ...form, percentage_eaten: parseInt(form.percentage_eaten) });
      setOpen(false);
      setForm({ meal_type: 'lunch', percentage_eaten: 75, food_description: '', appetite: '', entry_time: '', notes: '' });
    } finally { setSaving(false); }
  };

  const avgPct = entries.length ? Math.round(entries.reduce((s, e) => s + (e.percentage_eaten || 0), 0) / entries.length) : null;
  const pctColor = avgPct === null ? '' : avgPct >= 75 ? 'text-[#1F8F5F]' : avgPct >= 50 ? 'text-amber-600' : 'text-red-600';

  const PCT_OPTIONS = [0, 25, 50, 75, 100];

  return (
    <div className="space-y-4">
      {avgPct !== null && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-slate-500">Average eaten today</p>
            <p className={`text-xl font-bold ${pctColor}`}>{avgPct}%</p>
          </div>
          <ProgressBar value={avgPct} max={100} />
          {avgPct < 50 && (
            <div className="mt-2 flex items-center gap-1.5 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-800">Poor food intake — consider notifying manager and recording in daily notes</p>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-500">{entries.length} meal{entries.length !== 1 ? 's' : ''} recorded today</p>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
          <Plus className="h-3.5 w-3.5 mr-1" />Record meal
        </Button>
      </div>

      {open && (
        <div className="bg-[#D9F1F4]/20 rounded-2xl border border-[#0F5C5E]/20 p-4 space-y-3">
          <p className="text-xs font-semibold text-[#0F5C5E]">New food entry</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Meal</label>
              <select className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" value={form.meal_type} onChange={e => setForm(f => ({ ...f, meal_type: e.target.value }))}>
                {MEAL_TYPES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Time</label>
              <input type="time" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" value={form.entry_time} onChange={e => setForm(f => ({ ...f, entry_time: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Amount eaten</label>
            <div className="grid grid-cols-5 gap-2">
              {PCT_OPTIONS.map(p => (
                <button key={p} onClick={() => setForm(f => ({ ...f, percentage_eaten: p }))}
                  className={`py-3 rounded-xl text-sm font-bold transition-all
                    ${form.percentage_eaten === p
                      ? p >= 75 ? 'bg-[#1F8F5F] text-white' : p >= 50 ? 'bg-amber-500 text-white' : 'bg-red-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}>
                  {p}%
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Food description</label>
            <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" placeholder="e.g. Porridge and toast" value={form.food_description} onChange={e => setForm(f => ({ ...f, food_description: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Appetite</label>
            <div className="flex gap-2">
              {['Good','Fair','Poor','Refused'].map(a => (
                <button key={a} onClick={() => setForm(f => ({ ...f, appetite: a.toLowerCase() }))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${form.appetite === a.toLowerCase() ? 'bg-[#0F5C5E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {a}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
            <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" placeholder="Any notes..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={saving} onClick={handleAdd} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
              {saving ? 'Saving…' : 'Record meal'}
            </Button>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <p className="text-center text-xs text-slate-400 py-6">No food entries recorded for this date</p>
      ) : (
        <div className="space-y-2">
          {entries.map(e => <EntryRow key={e.id} entry={e} onDelete={id => onDelete(id)} isAdmin={isAdmin} />)}
        </div>
      )}
    </div>
  );
}

// ─── REPOSITIONING CHART ──────────────────────────────────────────────────────

function RepositioningTab({ entries, onAdd, date, isAdmin, onDelete }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ position: 'left_side', skin_check: 'intact', skin_check_area: '', entry_time: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    setSaving(true);
    try {
      await onAdd({ chart_type: 'repositioning', entry_date: date, ...form });
      setOpen(false);
      setForm({ position: 'right_side', skin_check: 'intact', skin_check_area: '', entry_time: '', notes: '' });
    } finally { setSaving(false); }
  };

  const skinConcerns = entries.filter(e => e.skin_check && e.skin_check !== 'intact');

  return (
    <div className="space-y-4">
      {/* Today summary */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Repositioning turns today</p>
            <p className="text-2xl font-bold text-slate-900">{entries.length}</p>
          </div>
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${entries.length >= 12 ? 'bg-[#D8E6D5]' : entries.length >= 6 ? 'bg-amber-100' : 'bg-red-100'}`}>
            <RefreshCw className={`h-6 w-6 ${entries.length >= 12 ? 'text-[#1F8F5F]' : entries.length >= 6 ? 'text-amber-600' : 'text-red-500'}`} />
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-1.5">2-hourly turns recommended (12 per 24h) — EPUAP guidelines</p>
        {skinConcerns.length > 0 && (
          <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-800 font-medium">{skinConcerns.length} skin concern{skinConcerns.length > 1 ? 's' : ''} noted — document in skin integrity chart and notify manager</p>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-500">{entries.length} turns recorded</p>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
          <Plus className="h-3.5 w-3.5 mr-1" />Record turn
        </Button>
      </div>

      {open && (
        <div className="bg-[#D9F1F4]/20 rounded-2xl border border-[#0F5C5E]/20 p-4 space-y-3">
          <p className="text-xs font-semibold text-[#0F5C5E]">Record repositioning</p>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Position</label>
            <div className="grid grid-cols-2 gap-2">
              {POSITIONS.map(p => (
                <button key={p.value} onClick={() => setForm(f => ({ ...f, position: p.value }))}
                  className={`py-2.5 px-3 rounded-xl text-xs font-medium text-left transition-all border-2
                    ${form.position === p.value ? 'border-[#0F5C5E] bg-[#D9F1F4]/40 text-[#0F5C5E]' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Skin check</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'intact',          label: 'Intact',         color: 'border-[#1F8F5F] bg-[#D8E6D5] text-[#1F8F5F]' },
                { value: 'reddened',        label: 'Reddened',       color: 'border-amber-400 bg-amber-50 text-amber-700' },
                { value: 'broken',          label: 'Broken skin',    color: 'border-red-400 bg-red-50 text-red-700' },
                { value: 'pressure_ulcer',  label: 'Pressure ulcer', color: 'border-red-600 bg-red-100 text-red-800' },
              ].map(s => (
                <button key={s.value} onClick={() => setForm(f => ({ ...f, skin_check: s.value }))}
                  className={`py-2.5 px-3 rounded-xl text-xs font-medium transition-all border-2
                    ${form.skin_check === s.value ? s.color : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          {form.skin_check !== 'intact' && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Area of concern</label>
              <input className="w-full px-3 py-2 rounded-lg border border-red-200 text-sm focus:ring-1 focus:ring-red-400" placeholder="e.g. Left heel, sacrum..." value={form.skin_check_area} onChange={e => setForm(f => ({ ...f, skin_check_area: e.target.value }))} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Time</label>
              <input type="time" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" value={form.entry_time} onChange={e => setForm(f => ({ ...f, entry_time: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
              <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={saving} onClick={handleAdd} className={`text-white ${form.skin_check !== 'intact' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#0F5C5E] hover:bg-[#0c4a4c]'}`}>
              {saving ? 'Saving…' : 'Record turn'}
            </Button>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <p className="text-center text-xs text-slate-400 py-6">No repositioning turns recorded for this date</p>
      ) : (
        <div className="space-y-2">
          {entries.map(e => <EntryRow key={e.id} entry={e} onDelete={id => onDelete(id)} isAdmin={isAdmin} />)}
        </div>
      )}
    </div>
  );
}

// ─── WEIGHT CHART ─────────────────────────────────────────────────────────────

function WeightTab({ entries, allEntries, onAdd, date, isAdmin, onDelete }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ weight_kg: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!form.weight_kg) { toast.error('Weight is required'); return; }
    setSaving(true);
    try {
      await onAdd({ chart_type: 'weight', entry_date: date, weight_kg: parseFloat(form.weight_kg), notes: form.notes });
      setOpen(false);
      setForm({ weight_kg: '', notes: '' });
    } finally { setSaving(false); }
  };

  // Last 10 weight entries across all time
  const history = allEntries.filter(e => e.chart_type === 'weight').slice(0, 10);
  const latest = history[0];
  const previous = history[1];
  const diff = latest && previous ? (latest.weight_kg - previous.weight_kg).toFixed(1) : null;

  return (
    <div className="space-y-4">
      {latest && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Last recorded weight</p>
              <p className="text-3xl font-bold text-slate-900">{latest.weight_kg} <span className="text-base font-normal text-slate-500">kg</span></p>
              <p className="text-xs text-slate-400 mt-0.5">{fmt(latest.entry_date)}</p>
            </div>
            {diff && (
              <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl ${parseFloat(diff) > 0 ? 'bg-amber-50 text-amber-700' : parseFloat(diff) < 0 ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-500'}`}>
                {parseFloat(diff) > 0 ? <TrendingUp className="h-4 w-4" /> : parseFloat(diff) < 0 ? <TrendingDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                <span className="text-sm font-bold">{diff > 0 ? '+' : ''}{diff} kg</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-500">Weight history</p>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
          <Plus className="h-3.5 w-3.5 mr-1" />Record weight
        </Button>
      </div>

      {open && (
        <div className="bg-[#D9F1F4]/20 rounded-2xl border border-[#0F5C5E]/20 p-4 space-y-3">
          <p className="text-xs font-semibold text-[#0F5C5E]">Record weight</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Weight (kg) *</label>
              <input type="number" step="0.1" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" placeholder="e.g. 68.5" value={form.weight_kg} onChange={e => setForm(f => ({ ...f, weight_kg: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
              <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" placeholder="Clothing, time of day..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={saving} onClick={handleAdd} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
              {saving ? 'Saving…' : 'Record weight'}
            </Button>
          </div>
        </div>
      )}

      {history.length === 0 ? (
        <p className="text-center text-xs text-slate-400 py-6">No weight measurements recorded yet</p>
      ) : (
        <div className="space-y-2">
          {history.map(e => <EntryRow key={e.id} entry={e} onDelete={id => onDelete(id)} isAdmin={isAdmin} />)}
        </div>
      )}
    </div>
  );
}

// ─── VITALS TAB ───────────────────────────────────────────────────────────────

function VitalsTab({ entries, onAdd, date, isAdmin, onDelete }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ systolic: '', diastolic: '', pulse: '', bp_position: 'sitting', temperature_celsius: '', temperature_route: 'ear', blood_glucose_mmol: '', meal_relation: '', oxygen_saturation: '', entry_time: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    const hasData = form.systolic || form.diastolic || form.pulse || form.temperature_celsius || form.blood_glucose_mmol || form.oxygen_saturation;
    if (!hasData) { toast.error('Enter at least one vital sign reading'); return; }
    setSaving(true);
    const payload = { chart_type: 'vitals', entry_date: date, entry_time: form.entry_time, notes: form.notes };
    if (form.systolic) payload.systolic = parseInt(form.systolic);
    if (form.diastolic) payload.diastolic = parseInt(form.diastolic);
    if (form.pulse) payload.pulse = parseInt(form.pulse);
    if (form.systolic || form.diastolic) payload.bp_position = form.bp_position;
    if (form.temperature_celsius) { payload.temperature_celsius = parseFloat(form.temperature_celsius); payload.temperature_route = form.temperature_route; }
    if (form.blood_glucose_mmol) { payload.blood_glucose_mmol = parseFloat(form.blood_glucose_mmol); payload.meal_relation = form.meal_relation; }
    if (form.oxygen_saturation) payload.oxygen_saturation = parseInt(form.oxygen_saturation);
    try {
      await onAdd(payload);
      setOpen(false);
      setForm({ systolic: '', diastolic: '', pulse: '', bp_position: 'sitting', temperature_celsius: '', temperature_route: 'ear', blood_glucose_mmol: '', meal_relation: '', oxygen_saturation: '', entry_time: '', notes: '' });
    } finally { setSaving(false); }
  };

  const latest = entries[0];
  const inputCls = 'w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]';

  return (
    <div className="space-y-4">
      {latest && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
          <p className="text-xs text-slate-500 mb-2">Latest readings — {fmt(latest.entry_date)} {fmtTime(latest.entry_time)}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {latest.systolic && (
              <div className="text-center p-2 bg-red-50 rounded-xl">
                <p className="text-[10px] text-slate-500">BP</p>
                <p className="text-lg font-bold text-red-700">{latest.systolic}/{latest.diastolic}</p>
                <p className="text-[10px] text-slate-400">mmHg</p>
              </div>
            )}
            {latest.pulse && (
              <div className="text-center p-2 bg-pink-50 rounded-xl">
                <p className="text-[10px] text-slate-500">Pulse</p>
                <p className="text-lg font-bold text-pink-700">{latest.pulse}</p>
                <p className="text-[10px] text-slate-400">bpm</p>
              </div>
            )}
            {latest.temperature_celsius && (
              <div className="text-center p-2 bg-amber-50 rounded-xl">
                <p className="text-[10px] text-slate-500">Temp</p>
                <p className="text-lg font-bold text-amber-700">{latest.temperature_celsius}°</p>
                <p className="text-[10px] text-slate-400">Celsius</p>
              </div>
            )}
            {latest.blood_glucose_mmol && (
              <div className="text-center p-2 bg-[#D9F1F4]/40 rounded-xl">
                <p className="text-[10px] text-slate-500">BGL</p>
                <p className="text-lg font-bold text-[#0F5C5E]">{latest.blood_glucose_mmol}</p>
                <p className="text-[10px] text-slate-400">mmol/L</p>
              </div>
            )}
            {latest.oxygen_saturation && (
              <div className="text-center p-2 bg-blue-50 rounded-xl">
                <p className="text-[10px] text-slate-500">SpO₂</p>
                <p className="text-lg font-bold text-blue-700">{latest.oxygen_saturation}%</p>
                <p className="text-[10px] text-slate-400">Oxygen</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-500">{entries.length} recording{entries.length !== 1 ? 's' : ''} today</p>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
          <Plus className="h-3.5 w-3.5 mr-1" />Record vitals
        </Button>
      </div>

      {open && (
        <div className="bg-[#D9F1F4]/20 rounded-2xl border border-[#0F5C5E]/20 p-4 space-y-4">
          <p className="text-xs font-semibold text-[#0F5C5E]">Record vital signs</p>
          {/* Blood pressure */}
          <div>
            <p className="text-xs font-medium text-slate-600 mb-2">Blood pressure</p>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="block text-[10px] text-slate-500 mb-1">Systolic</label><input type="number" className={inputCls} placeholder="120" value={form.systolic} onChange={e => setForm(f => ({ ...f, systolic: e.target.value }))} /></div>
              <div><label className="block text-[10px] text-slate-500 mb-1">Diastolic</label><input type="number" className={inputCls} placeholder="80" value={form.diastolic} onChange={e => setForm(f => ({ ...f, diastolic: e.target.value }))} /></div>
              <div><label className="block text-[10px] text-slate-500 mb-1">Pulse (bpm)</label><input type="number" className={inputCls} placeholder="72" value={form.pulse} onChange={e => setForm(f => ({ ...f, pulse: e.target.value }))} /></div>
            </div>
          </div>
          {/* Temperature & BGL */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Temperature (°C)</label>
              <input type="number" step="0.1" className={inputCls} placeholder="36.6" value={form.temperature_celsius} onChange={e => setForm(f => ({ ...f, temperature_celsius: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Blood glucose (mmol/L)</label>
              <input type="number" step="0.1" className={inputCls} placeholder="5.5" value={form.blood_glucose_mmol} onChange={e => setForm(f => ({ ...f, blood_glucose_mmol: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">SpO₂ (%)</label>
              <input type="number" className={inputCls} placeholder="98" value={form.oxygen_saturation} onChange={e => setForm(f => ({ ...f, oxygen_saturation: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Time</label>
              <input type="time" className={inputCls} value={form.entry_time} onChange={e => setForm(f => ({ ...f, entry_time: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
            <input className={inputCls} placeholder="Any observations..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={saving} onClick={handleAdd} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
              {saving ? 'Saving…' : 'Record vitals'}
            </Button>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <p className="text-center text-xs text-slate-400 py-6">No vital sign recordings for this date</p>
      ) : (
        <div className="space-y-2">
          {entries.map(e => <EntryRow key={e.id} entry={e} onDelete={id => onDelete(id)} isAdmin={isAdmin} />)}
        </div>
      )}
    </div>
  );
}

// ─── BOWEL CHART ─────────────────────────────────────────────────────────────

function BowelTab({ entries, onAdd, date, isAdmin, onDelete }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ bristol_type: 4, incontinent: false, amount: '', entry_time: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    setSaving(true);
    try {
      await onAdd({ chart_type: 'bowel', entry_date: date, ...form, bristol_type: parseInt(form.bristol_type) });
      setOpen(false);
      setForm({ bristol_type: 4, incontinent: false, amount: '', entry_time: '', notes: '' });
    } finally { setSaving(false); }
  };

  const bristolColors = { 1: 'bg-red-100 text-red-700', 2: 'bg-amber-100 text-amber-700', 3: 'bg-[#D8E6D5] text-[#1F8F5F]', 4: 'bg-[#D8E6D5] text-[#1F8F5F]', 5: 'bg-amber-50 text-amber-600', 6: 'bg-orange-100 text-orange-700', 7: 'bg-red-100 text-red-700' };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-500">{entries.length} bowel opening{entries.length !== 1 ? 's' : ''} recorded today</p>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
          <Plus className="h-3.5 w-3.5 mr-1" />Record opening
        </Button>
      </div>

      {open && (
        <div className="bg-[#D9F1F4]/20 rounded-2xl border border-[#0F5C5E]/20 p-4 space-y-3">
          <p className="text-xs font-semibold text-[#0F5C5E]">Record bowel opening</p>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Bristol stool type</label>
            <div className="space-y-1.5">
              {BRISTOL_TYPES.map(b => (
                <button key={b.value} onClick={() => setForm(f => ({ ...f, bristol_type: b.value }))}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all border-2
                    ${form.bristol_type === b.value
                      ? `${bristolColors[b.value]} border-current`
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}>
                  <span className="text-sm font-bold w-14 flex-shrink-0">{b.label}</span>
                  <span className="text-xs">{b.desc}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Amount</label>
              <select className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}>
                <option value="">—</option>
                {['small','medium','large'].map(a => <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Time</label>
              <input type="time" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" value={form.entry_time} onChange={e => setForm(f => ({ ...f, entry_time: e.target.value }))} />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.incontinent} onChange={e => setForm(f => ({ ...f, incontinent: e.target.checked }))} className="accent-[#0F5C5E]" />
            <span className="text-xs text-slate-700 font-medium">Incontinent episode</span>
          </label>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
            <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={saving} onClick={handleAdd} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
              {saving ? 'Saving…' : 'Record opening'}
            </Button>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <p className="text-center text-xs text-slate-400 py-6">No bowel recordings for this date</p>
      ) : (
        <div className="space-y-2">
          {entries.map(e => <EntryRow key={e.id} entry={e} onDelete={id => onDelete(id)} isAdmin={isAdmin} />)}
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function MonitoringCharts({ serviceUserId }) {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'branch_manager'].includes(user?.role);

  const [activeTab, setActiveTab] = useState('fluid');
  const [date, setDate] = useState(todayStr());
  const [entries, setEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
  const today = todayStr();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dayRes, summaryRes, allRes] = await Promise.all([
        axios.get(`${API}/service-users/${serviceUserId}/monitoring/entries`, {
          params: { date_from: date, date_to: date, limit: 200 },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/service-users/${serviceUserId}/monitoring/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        // Load weight history (last 90 days)
        axios.get(`${API}/service-users/${serviceUserId}/monitoring/entries`, {
          params: { chart_type: 'weight', limit: 20 },
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setEntries(dayRes.data || []);
      setSummary(summaryRes.data);
      setAllEntries(allRes.data || []);
    } catch {
      toast.error('Could not load monitoring data');
    } finally {
      setLoading(false);
    }
  }, [serviceUserId, date, token]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (payload) => {
    await axios.post(
      `${API}/service-users/${serviceUserId}/monitoring/entries`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    await load();
  };

  const handleDelete = async (entryId) => {
    try {
      await axios.delete(
        `${API}/service-users/${serviceUserId}/monitoring/entries/${entryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Entry deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  const tabEntries = entries.filter(e => e.chart_type === activeTab);
  const isToday = date === today;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-[#0F5C5E]" />
            Monitoring Charts
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            CQC Reg 14 · NICE CG32 · Fluid target: {FLUID_TARGET_ML}ml/day
          </p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100">
          <RefreshCw className={`h-4 w-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Summary ribbon */}
      {summary && !loading && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {[
            { label: 'Fluid today', value: `${summary.fluid_today_ml}ml`, sub: `${summary.fluid_pct_today}% of target`, warn: summary.fluid_pct_today < 50 },
            { label: 'Repositioning', value: summary.repositioning_count_today, sub: 'turns today', warn: summary.repositioning_count_today < 4 },
            { label: 'Food avg', value: summary.food_avg_pct_today !== null ? `${summary.food_avg_pct_today}%` : '—', sub: 'eaten today', warn: summary.food_avg_pct_today !== null && summary.food_avg_pct_today < 50 },
            { label: 'Last weight', value: summary.last_weight ? `${summary.last_weight.weight_kg}kg` : '—', sub: summary.last_weight ? fmt(summary.last_weight.entry_date) : 'not recorded', warn: false },
            { label: 'Last BP', value: summary.last_bp ? `${summary.last_bp.systolic}/${summary.last_bp.diastolic}` : '—', sub: summary.last_bp ? fmt(summary.last_bp.entry_date) : 'not recorded', warn: false },
            { label: 'Last bowel', value: summary.last_bowel ? `Type ${summary.last_bowel.bristol_type}` : '—', sub: summary.last_bowel ? fmt(summary.last_bowel.entry_date) : 'not recorded', warn: false },
          ].map(c => (
            <div key={c.label} className={`rounded-xl border p-3 text-center ${c.warn ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
              <p className={`text-base font-bold ${c.warn ? 'text-amber-700' : 'text-slate-900'}`}>{c.value}</p>
              <p className={`text-[10px] ${c.warn ? 'text-amber-600' : 'text-slate-500'}`}>{c.label}</p>
              <p className="text-[9px] text-slate-400">{c.sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* Date navigator */}
      <div className="flex items-center gap-2 justify-center">
        <button onClick={() => setDate(d => addDays(d, -1))} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <ChevronLeft className="h-4 w-4 text-slate-500" />
        </button>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={date}
            max={today}
            onChange={e => setDate(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E] text-slate-700"
          />
          {!isToday && (
            <button onClick={() => setDate(today)} className="text-xs text-[#0F5C5E] hover:underline px-2">Today</button>
          )}
          {isToday && <Badge className="bg-[#D9F1F4] text-[#0F5C5E] text-xs">Today</Badge>}
        </div>
        <button onClick={() => setDate(d => { const nd = addDays(d, 1); return nd <= today ? nd : d; })} disabled={isToday}
          className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors">
          <ChevronRight className="h-4 w-4 text-slate-500" />
        </button>
      </div>

      {/* Chart tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl overflow-x-auto">
        {TABS.map(t => {
          const tabCount = entries.filter(e => e.chart_type === t.id).length;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0
                ${activeTab === t.id
                  ? 'bg-white text-[#0F5C5E] shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
                }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
              {tabCount > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === t.id ? 'bg-[#D9F1F4] text-[#0F5C5E]' : 'bg-slate-200 text-slate-600'}`}>
                  {tabCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-[#0F5C5E] border-t-transparent animate-spin" />
            <p className="text-xs text-slate-400">Loading monitoring data…</p>
          </div>
        </div>
      ) : (
        <>
          {activeTab === 'fluid'         && <FluidTab         entries={tabEntries} onAdd={handleAdd} date={date} isAdmin={isAdmin} onDelete={handleDelete} />}
          {activeTab === 'food'          && <FoodTab          entries={tabEntries} onAdd={handleAdd} date={date} isAdmin={isAdmin} onDelete={handleDelete} />}
          {activeTab === 'repositioning' && <RepositioningTab entries={tabEntries} onAdd={handleAdd} date={date} isAdmin={isAdmin} onDelete={handleDelete} />}
          {activeTab === 'weight'        && <WeightTab        entries={tabEntries} allEntries={allEntries} onAdd={handleAdd} date={date} isAdmin={isAdmin} onDelete={handleDelete} />}
          {activeTab === 'vitals'        && <VitalsTab        entries={tabEntries} onAdd={handleAdd} date={date} isAdmin={isAdmin} onDelete={handleDelete} />}
          {activeTab === 'bowel'         && <BowelTab         entries={tabEntries} onAdd={handleAdd} date={date} isAdmin={isAdmin} onDelete={handleDelete} />}
        </>
      )}
    </div>
  );
}
